import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ChevronDown, ChevronLeft, ChevronRight, Instagram } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { ChespiHospitalityLogo } from "@/components/Logo";
import { useLang } from "@/i18n";

// Public assets — encode so filenames with spaces (the Bonus Reel video) resolve.
const pub = (f) => `${process.env.PUBLIC_URL}/${encodeURIComponent(f)}`;

const DIPLOMATIC_SITE = "https://complexdiplomatic.ro/";
const DIPLOMATIC_IG = "https://www.instagram.com/diplomaticmamaia/";
const ORO_IG = "https://www.instagram.com/oromamaiarestaurant/";
const ENIGMA_BOOKING =
  "https://www.booking.com/hotel/ro/enigma-resort.ro.html?aid=311984&label=enigma-resort-dYGjTQJXtgLj2SvI3b%2AS1AS475885601934%3Apl%3Ata%3Ap1%3Ap2%3Aac%3Aap%3Aneg%3Afi%3Atikwd-39980504453%3Alp9191474%3Ali%3Adec%3Adm%3Appccp%3DUmFuZG9tSVYkc2RlIyh9YTQUGSsRwx9_3qo3uPTHyoo&sid=4d4f0d1e9bd8d2e1276121506a67eff8";

// Tiny uppercase overline in deep gold — the recurring light-theme label.
function Overline({ children }) {
  return <p className="text-[11px] font-medium uppercase tracking-[0.36em] text-[#a9842f]">{children}</p>;
}

// A large image with its title written across it + a slow hover zoom. The
// caller positions it (col-span / offsets / height) through `className`.
function Plate({ src, alt, caption, className = "", imgClass = "object-cover", delay = 0 }) {
  return (
    <Reveal delay={delay} className={`group relative overflow-hidden ${className}`}>
      <img
        src={pub(src)}
        alt={alt || caption || ""}
        loading="lazy"
        className={`h-full w-full ${imgClass} transition-transform [transition-duration:1200ms] ease-out group-hover:scale-[1.04]`}
      />
      {caption ? (
        <>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/55 to-transparent" />
          <div className="absolute bottom-0 left-0 p-5 md:p-7">
            <span className="mb-2 block h-px w-8 bg-[#d4af37]" />
            <span className="font-serif text-lg font-light text-white md:text-2xl">{caption}</span>
          </div>
        </>
      ) : null}
    </Reveal>
  );
}

// Auto-advancing, white-theme image carousel with an overlaid label.
function Carousel({ slides, caption }) {
  const [idx, setIdx] = useState(0);
  const n = slides.length;
  const go = useCallback((i) => setIdx((i + n) % n), [n]);
  const next = useCallback(() => setIdx((i) => (i + 1) % n), [n]);

  useEffect(() => {
    const id = setInterval(next, 5000);
    return () => clearInterval(id);
  }, [next]);

  return (
    <div className="relative w-full overflow-hidden">
      <div
        className="flex transition-transform duration-700 ease-out"
        style={{ transform: `translateX(-${idx * 100}%)` }}
      >
        {slides.map((s) => (
          <img key={s} src={pub(s)} alt={caption || ""} className="h-[56vh] w-full flex-shrink-0 object-cover md:h-[80vh]" />
        ))}
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/55 to-transparent" />
      {caption ? (
        <div className="absolute bottom-0 left-0 p-5 md:p-7">
          <span className="mb-2 block h-px w-8 bg-[#d4af37]" />
          <span className="font-serif text-lg font-light text-white md:text-2xl">{caption}</span>
        </div>
      ) : null}

      <button
        onClick={() => go(idx - 1)}
        aria-label="Previous"
        className="absolute left-4 top-1/2 -translate-y-1/2 border border-white/40 bg-black/20 p-2 text-white backdrop-blur-sm transition-all hover:border-white hover:bg-black/40"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        onClick={next}
        aria-label="Next"
        className="absolute right-4 top-1/2 -translate-y-1/2 border border-white/40 bg-black/20 p-2 text-white backdrop-blur-sm transition-all hover:border-white hover:bg-black/40"
      >
        <ChevronRight size={20} />
      </button>

      <div className="absolute bottom-6 right-6 flex gap-2.5 md:bottom-10 md:right-10">
        {slides.map((s, i) => (
          <button
            key={s}
            onClick={() => go(i)}
            aria-label={`Slide ${i + 1}`}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === idx ? "w-7 bg-white" : "w-1.5 bg-white/50 hover:bg-white/80"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

export default function TourismPage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const h = t.tourism;

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const backHome = () => {
    navigate("/");
    setTimeout(() => document.getElementById("sectors")?.scrollIntoView({ behavior: "smooth" }), 90);
  };

  return (
    <main className="bg-white text-ink">
      {/* ───────────── HERO — Diplomatic, full-screen Bonus Reel (Sunrise & Sunset) ───────────── */}
      <section className="relative h-[100svh] w-full overflow-hidden bg-black">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          src={pub("Diplomatic - Bonus Reel.mp4")}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-44 bg-gradient-to-b from-black/55 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/75 via-black/15 to-transparent" />

        <div className="container-luxe absolute inset-x-0 bottom-0 pb-20 md:pb-28">
          <Reveal>
            <ChespiHospitalityLogo markSize={20} textClass="text-[11px] sm:text-xs" />
            <h1 className="mt-4 font-serif text-5xl font-light leading-[0.98] text-white sm:text-6xl lg:text-7xl">
              {h.heroBrand}
            </h1>
            <p className="mt-4 max-w-xl font-serif text-xl italic text-white/80 sm:text-2xl">{h.heroTagline}</p>
          </Reveal>
        </div>

        <div className="pointer-events-none absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60">
          <ChevronDown size={24} className="animate-bounce" />
        </div>
      </section>

      {/* ───────────── Diplomatic — the story ───────────── */}
      <section className="bg-white py-24 md:py-32">
        <div className="container-luxe">
          <Reveal className="mx-auto max-w-3xl">
            <Overline>{h.heroBrand}</Overline>
            <h2 className="mt-4 font-serif text-3xl font-light leading-snug text-ink sm:text-4xl md:text-5xl">
              {h.diploLead}
            </h2>
            <div className="mt-7 space-y-4 text-sm leading-relaxed text-ink/60 md:text-base">
              {h.diploBody.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
            <div className="mt-9 flex flex-wrap items-center gap-5">
              <a
                href={DIPLOMATIC_SITE}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[#a9842f] transition-colors hover:text-ink"
              >
                {h.diploCta}
                <ArrowUpRight size={14} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
              <span className="h-3 w-px bg-ink/15" />
              <a
                href={DIPLOMATIC_IG}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[#a9842f] transition-colors hover:text-ink"
              >
                <Instagram size={14} /> @diplomaticmamaia
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────────── Salon Sunrise & Sunset — asymmetric, captioned ───────────── */}
      <section className="bg-white pb-12 md:pb-16">
        <div className="container-luxe grid items-start gap-4 md:grid-cols-12 md:gap-6">
          <Plate
            src="SalonSunriseIntrare.jpg"
            caption={h.salonsLabel}
            className="h-[52vh] md:col-span-7 md:h-[78vh]"
          />
          <div className="flex flex-col gap-4 md:col-span-5 md:mt-16 md:gap-6">
            <Plate src="salonSunrise.jpg" caption="Salon Sunrise" className="h-[30vh] md:h-[36vh]" />
            <Plate src="salonulSunset.jpg" caption="Salon Sunset" delay={0.1} className="h-[30vh] md:h-[36vh]" />
          </div>
        </div>
      </section>

      {/* ───────────── Stats ───────────── */}
      <section className="bg-white py-20 md:py-28">
        <div className="container-luxe">
          <Reveal>
            <div className="grid grid-cols-2 border-y border-ink/10 md:grid-cols-4">
              {h.stats.map((s) => (
                <div
                  key={s.label}
                  className="border-ink/10 px-4 py-9 text-center [&:nth-child(2n)]:border-l [&:nth-child(n+3)]:border-t md:border-l md:[&:nth-child(4n+1)]:border-l-0 md:[&:nth-child(n+3)]:border-t-0"
                >
                  <div className="font-serif text-4xl font-light text-ink md:text-5xl">{s.value}</div>
                  <div className="mt-3 text-[10px] uppercase tracking-[0.24em] text-ink/45">{s.label}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────────── Salon Lumière — the portrait reel + the hall, captioned ───────────── */}
      <section className="bg-white pb-12 pt-4 md:pb-20">
        <div className="container-luxe grid items-center gap-8 md:grid-cols-12 md:gap-10">
          {/* The portrait reel, kept upright and uncropped */}
          <Reveal className="md:col-span-5">
            <div className="mx-auto w-full max-w-[380px]">
              <div className="mb-4 flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.32em] text-[#a9842f]">
                <span className="h-px w-8 bg-[#b5952f]/60" /> {h.lumiereLabel}
              </div>
              <div className="relative aspect-[9/16] w-full overflow-hidden bg-[#f7f4ee]">
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  poster={pub("lamierePOZA.jpeg")}
                  src={pub("lumiere.mp4")}
                  className="absolute inset-0 h-full w-full object-contain"
                />
              </div>
            </div>
          </Reveal>

          {/* The hall, captioned "Salon Lumière" */}
          <Plate
            src="lamierePOZA.jpeg"
            caption={h.lumiereLabel}
            delay={0.1}
            className="h-[52vh] md:col-span-7 md:h-[64vh]"
          />
        </div>
      </section>

      {/* ───────────── Enigma Resort — intro + carousel ───────────── */}
      <section className="bg-white pt-28 md:pt-40">
        <Reveal>
          <div className="container-luxe mx-auto max-w-2xl text-center">
            <Overline>{h.enigmaBrand}</Overline>
            <h2 className="mt-4 font-serif text-2xl font-light leading-snug text-ink sm:text-3xl md:text-[2.5rem]">
              {h.enigmaLead}
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-sm leading-relaxed text-ink/55 md:text-base">{h.enigmaText}</p>
            <a
              href={ENIGMA_BOOKING}
              target="_blank"
              rel="noopener noreferrer"
              className="group mt-8 inline-flex items-center gap-2 border border-ink/20 px-6 py-3 text-xs uppercase tracking-[0.2em] text-ink/70 transition-colors duration-300 hover:border-[#b5952f] hover:text-[#a9842f]"
            >
              {h.enigmaCta}
              <ArrowUpRight size={14} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          </div>
        </Reveal>
      </section>

      <section className="bg-white pb-12 pt-12 md:pt-16">
        <Reveal>
          <div className="container-luxe">
            <Carousel
              slides={["EnigmaIntrare.jpg", "enigmaResort.jpg", "enigmaResort3.jpg", "enigmaResort4.jpg", "interiorEnigma1.jpg", "interiorEnigma2.jpg"]}
              caption={h.enigmaBrand}
            />
          </div>
        </Reveal>
      </section>

      {/* ───────────── Oro Restaurant — fine dining inside the Diplomatic complex ───────────── */}
      <section className="bg-white pt-28 md:pt-40">
        <Reveal>
          <div className="container-luxe mx-auto max-w-2xl text-center">
            <Overline>{h.oroBrand}</Overline>
            <h2 className="mt-4 font-serif text-2xl font-light leading-snug text-ink sm:text-3xl md:text-[2.5rem]">
              {h.oroLead}
            </h2>
            <div className="mt-6 space-y-4 text-sm leading-relaxed text-ink/55 md:text-base">
              {h.oroBody.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
            <a
              href={ORO_IG}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[#a9842f] transition-colors hover:text-ink"
            >
              <Instagram size={14} /> @oromamaiarestaurant
            </a>
          </div>
        </Reveal>
      </section>

      <section className="bg-white pb-20 pt-12 md:pb-28 md:pt-16">
        {/* exterior + live cooking, offset */}
        <div className="container-luxe grid items-start gap-4 md:grid-cols-12 md:gap-6">
          <Plate
            src="OroRestaurantjpg.jpg"
            caption="Oro Restaurant"
            className="h-[48vh] md:col-span-7 md:h-[68vh]"
          />
          <Plate
            src="liveCookingOro.jpeg"
            caption="Live cooking"
            delay={0.1}
            className="h-[40vh] md:col-span-5 md:mt-20 md:h-[52vh]"
          />
        </div>
        {/* the plates, offset against each other */}
        <div className="container-luxe mt-4 grid items-start gap-4 md:mt-6 md:grid-cols-12 md:gap-6">
          <Plate
            src="pozaMancareOro.jpeg"
            caption="Fine dining"
            className="h-[52vh] md:col-span-4 md:col-start-2 md:h-[66vh]"
          />
          <Plate
            src="PozaMancareOro2.jpeg"
            delay={0.1}
            className="h-[52vh] md:col-span-4 md:col-start-8 md:mt-16 md:h-[66vh]"
          />
        </div>
      </section>

      {/* ───────────── Cinque Sensi — authentic Italian pizzeria in Constanța ───────────── */}
      <section className="bg-white pt-28 md:pt-40">
        <Reveal>
          <div className="container-luxe mx-auto max-w-2xl text-center">
            <Overline>{h.cinqueBrand}</Overline>
            <h2 className="mt-4 font-serif text-2xl font-light leading-snug text-ink sm:text-3xl md:text-[2.5rem]">
              {h.cinqueLead}
            </h2>
            <div className="mt-6 space-y-4 text-sm leading-relaxed text-ink/55 md:text-base">
              {h.cinqueBody.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      <section className="bg-white pb-12 pt-12 md:pb-20 md:pt-16">
        <div className="container-luxe">
          <Plate
            src="cinqueSensi.jpeg"
            caption={h.cinqueBrand}
            className="mx-auto h-[60vh] w-full max-w-3xl md:h-[80vh]"
          />
        </div>
      </section>

      {/* ───────────── Closing — minimal back (each Instagram lives in its own section) ───────────── */}
      <section className="bg-white py-24 md:py-32">
        <Reveal>
          <div className="container-luxe text-center">
            <span className="mx-auto mb-8 block h-px w-12 bg-[#b5952f]/60" />
            <button
              onClick={backHome}
              className="inline-flex items-center gap-2 border border-ink/20 px-6 py-3 text-xs uppercase tracking-[0.2em] text-ink/70 transition-colors duration-300 hover:border-[#b5952f] hover:text-[#a9842f]"
            >
              <ArrowLeft size={16} /> {h.back}
            </button>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
